import { Controller } from "@hotwired/stimulus"
import IMask from "imask"

// Named presets for Brazilian field patterns.
// Dynamic masks (arrays) let IMask switch between patterns as the user types.
const PRESETS = {
  cpf_cnpj: {
    mask: [
      { mask: "000.000.000-00" },
      { mask: "00.000.000/0000-00" }
    ]
  },
  phone: {
    mask: [
      { mask: "(00) 0000-0000" },
      { mask: "(00) 00000-0000" }
    ]
  },
  cellphone: { mask: "(00) 00000-0000" },
  cep:       { mask: "00000-000" },
  date:      { mask: "00/00/0000" },
  currency: {
    mask: Number,
    scale: 2,
    thousandsSeparator: '.',
    radix: ',',
    mapToRadix: ['.'],
    normalizeZeros: true,
    padFractionalZeros: true
  }
}

export default class extends Controller {
  static values = { preset: String }

  connect() {
    if (this.presetValue === 'currency') {
      this.#setupCurrencyMask()
    } else {
      this.#mask = IMask(this.element, PRESETS[this.presetValue])
    }
  }

  disconnect() {
    this.#mask?.destroy()
    this.#hiddenInput?.remove()
    this.#hiddenInput = null
  }

  // Public API for other controllers (e.g. account_profile_autofill_controller)
  // to set a display string on a non-currency pattern mask (cpf_cnpj, cep,
  // phone, …) and have IMask reformat it, rather than poking .value directly
  // and leaving IMask's internal state out of sync.
  setDisplayValue(value) {
    if (!this.#mask || this.presetValue === 'currency') return
    this.#programmatic = true
    this.#mask.value = value || ''
    this.#programmatic = false
  }

  // Public API for other controllers (e.g. order_form_controller) to set
  // a numeric value programmatically and have the display update formatted.
  // Suppresses the mask:accept event so programmatic updates don't
  // re-trigger the listeners that respond to user-driven changes.
  setValue(num) {
    if (!this.#mask) return
    this.#programmatic = true
    if (num === null || num === undefined || num === '') {
      this.#mask.unmaskedValue = ''
      this.#syncRaw('')
    } else {
      const n = typeof num === 'number' ? num : parseFloat(num)
      this.#mask.typedValue = isNaN(n) ? 0 : n
      this.#syncRaw(isNaN(n) ? '' : String(n))
    }
    this.#programmatic = false
  }

  #mask = null
  #hiddenInput = null
  #programmatic = false

  #setupCurrencyMask() {
    const name = this.element.name
    const min  = this.element.getAttribute('min')
    const initialValue = this.element.value

    // Build options; respect the field's min attribute if present
    const options = { ...PRESETS.currency }
    if (min !== null) options.min = parseFloat(min)

    // Create a hidden input that carries the field name and submits the raw
    // decimal value. The visible input's name is removed so only the hidden
    // one is serialized by the form.
    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = name
    if (initialValue) hidden.value = initialValue
    this.element.insertAdjacentElement('afterend', hidden)
    this.#hiddenInput = hidden

    this.element.removeAttribute('name')
    this.element.removeAttribute('min')
    this.element.removeAttribute('step')
    this.element.setAttribute('inputmode', 'decimal')
    this.element.autocomplete = 'off'

    this.#mask = IMask(this.element, options)

    // Format the initial value that came from the server
    const num = parseFloat(initialValue)
    if (!isNaN(num)) {
      this.#mask.typedValue = num
      this.#syncRaw(String(num))
    }

    this.#mask.on('accept', () => {
      const typed = this.#mask.typedValue
      this.#syncRaw((typeof typed === 'number' && !isNaN(typed)) ? String(typed) : '')

      // Notify listeners (e.g. order_form_controller) that the raw value is
      // now fully synced. Fired only for user-driven changes — using this
      // instead of the native "input" event avoids a race where a sibling
      // controller's "input" listener reads dataset.rawValue before IMask
      // has finished processing the keystroke.
      if (!this.#programmatic) {
        this.element.dispatchEvent(new CustomEvent('mask:accept', { bubbles: true }))
      }
    })
  }

  // Keeps the hidden input and dataset.rawValue in sync with the raw number.
  // dataset.rawValue is consumed by order_form_controller to avoid parsing
  // the Brazilian-formatted display string with parseFloat.
  #syncRaw(raw) {
    if (this.#hiddenInput) this.#hiddenInput.value = raw
    this.element.dataset.rawValue = raw
  }
}
